import { useEffect, useRef, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Hash, Send, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

export default function Chat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/chat/channels").then(r => {
      setChannels(r.data);
      if (r.data.length && !active) setActive(r.data[0]);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    let stop = false;
    const fetchMsgs = () => api.get(`/chat/channels/${active.id}/messages`).then(r => {
      if (!stop) setMessages(r.data);
    });
    fetchMsgs();
    const t = setInterval(fetchMsgs, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [active]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const r = await api.post(`/chat/channels/${active.id}/messages`, { text });
    setMessages(prev => [...prev, r.data]);
    setText("");
  };

  const createChannel = async () => {
    if (!newName) return toast.error("Name required");
    const r = await api.post("/chat/channels", { name: newName, type: "channel" });
    setChannels(prev => [...prev, r.data]);
    setActive(r.data);
    setOpen(false);
    setNewName("");
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <PageHeader title="Team Chat" subtitle="Company-wide and department channels" />

      <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden flex min-h-[600px]">
        <aside className="w-64 border-r border-slate-200 bg-slate-50 flex flex-col">
          <div className="p-3 border-b border-slate-200 flex justify-between items-center">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Channels</div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="icon" variant="ghost" className="h-6 w-6" data-testid="new-channel-btn"><Plus className="w-3.5 h-3.5" /></Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New channel</DialogTitle></DialogHeader>
                <Input placeholder="channel-name" value={newName} onChange={e => setNewName(e.target.value)} data-testid="channel-name-input" />
                <DialogFooter>
                  <Button onClick={createChannel} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {channels.map(c => (
              <button
                key={c.id}
                onClick={() => setActive(c)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm hover:bg-white transition-colors ${active?.id === c.id ? "bg-white text-brand-aqua-hover font-medium" : "text-slate-700"}`}
                data-testid={`channel-${c.name}`}
              >
                <Hash className="w-3.5 h-3.5 text-slate-400" />
                <span>{c.name}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="flex-1 flex flex-col">
          <div className="border-b border-slate-200 px-4 py-3 flex items-center gap-2">
            <Hash className="w-4 h-4 text-slate-400" />
            <div className="font-heading font-semibold text-slate-900">{active?.name || "Select channel"}</div>
            {active?.department && <span className="text-xs text-slate-500 ml-2">· {active.department}</span>}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30">
            {messages.map(m => (
              <div key={m.id} className="flex gap-2.5 animate-fade-in">
                <Avatar className="w-8 h-8 mt-0.5">
                  <AvatarImage src={m.author_avatar} />
                  <AvatarFallback>{m.author_name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-xs">
                    <strong className="text-slate-800">{m.author_name}</strong>
                    <span className="text-slate-400 ml-2">{formatDateTime(m.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5">{m.text}</div>
                </div>
              </div>
            ))}
            {messages.length === 0 && <div className="text-center text-slate-400 text-sm py-8">Be the first to say hi 👋</div>}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-slate-200 p-3 flex gap-2">
            <Input placeholder={active ? `Message #${active.name}` : "Select a channel"}
                   value={text} onChange={e => setText(e.target.value)}
                   onKeyDown={e => e.key === "Enter" && send()}
                   disabled={!active}
                   data-testid="chat-message-input" />
            <Button onClick={send} disabled={!text.trim()} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="send-message-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
